import {convertFromRaw, convertToRaw, EditorState} from 'draft-js';
import {draftToMarkdown} from 'markdown-draft-js';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Editor} from 'react-draft-wysiwyg';
import styled from 'styled-components';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {RichTextToolbar} from './rich-text-toolbar';
import {markdownToRawContent} from './rich-text.utils';

const Wrapper = styled.div<{
  $error?: boolean;
  $checkin?: boolean;
  $readOnly?: boolean;
  $minHeight?: number;
}>`
  width: 100%;

  .input {
    padding: 2px 0;
  }

  .custom-editor-wrapper {
    min-height: ${({$minHeight}) => `${$minHeight || 120}px`};
    overflow: visible;
    border: ${({$checkin, $error, theme}) =>
      $checkin
        ? `1px solid ${$error ? theme.colors.red400 : theme.colors.borderDark}`
        : 'none'};
    border-radius: ${({$checkin}) => ($checkin ? '10px' : '0')};
    background: ${({$readOnly, theme}) =>
      $readOnly ? theme.colors.backgroundLight : theme.colors.white};
    color: ${({theme}) => theme.colors.blackTwo};
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    letter-spacing: normal;
    line-height: 24px;
  }

  .rdw-editor-toolbar.beam-rich-text-toolbar {
    box-sizing: border-box;
    width: 100%;
    padding: 8px 12px;
    border-bottom: 1px solid ${({theme}) => theme.colors.borderLight};
    border-radius: 10px 10px 0 0;
    background: ${({theme}) => theme.colors.white};
  }

  .custom-editor-input {
    box-sizing: border-box;
    min-height: ${({$minHeight}) => `${$minHeight || 120}px`};
    max-height: 800px;
    padding: ${({$checkin}) => ($checkin ? '0 16px 12px' : '12px 16px')};
    overflow-y: auto;
  }

  .custom-editor-input .public-DraftEditor-content {
    min-height: ${({$minHeight}) => `${$minHeight || 120}px`};
  }

  .custom-editor-input .public-DraftEditorPlaceholder-root {
    color: ${({theme}) => theme.colors.textBody};
    font-weight: 400;
  }

  .custom-editor-input a {
    color: ${({theme}) => theme.colors.purple300};
    text-decoration: underline;
  }

  .rdw-editor-toolbar {
    margin: 0;
  }

  ${({$checkin}) =>
    !$checkin &&
    `
      .custom-editor-input {
        padding: 5px 10px;
      }
    `}
`;

const Helper = styled.p`
  margin-top: 6px;
  color: ${({theme}) => theme.colors.red400};
  font-size: 12px;
  line-height: 18px;
`;

interface RichTextEditorProps {
  onChange: (data: {text: string; markdown: string}) => void;
  placeholder?: string;
  value?: string | null;
  variant?: 'default' | 'checkin';
  readOnly?: boolean;
  error?: boolean;
  helper?: string;
  minHeight?: number;
  ariaLabel?: string;
}

const createEditorState = (value?: string | null) => {
  const raw = markdownToRawContent(value);
  return raw
    ? EditorState.createWithContent(convertFromRaw(raw))
    : EditorState.createEmpty();
};

export const RichTextEditor = ({
  onChange,
  placeholder,
  value,
  variant = 'default',
  readOnly = false,
  error = false,
  helper,
  minHeight = 120,
  ariaLabel = 'Rich text editor',
}: RichTextEditorProps) => {
  const [editorState, setEditorState] = useState(() =>
    createEditorState(value),
  );
  const lastEmittedMarkdown = useRef<string | null>(null);

  useEffect(() => {
    const nextValue = value || '';
    if (nextValue === lastEmittedMarkdown.current) return;
    setEditorState(createEditorState(nextValue));
  }, [value]);

  const toolbarButtons = useMemo(
    () => [
      <RichTextToolbar
        key="beam-rich-text-toolbar"
        editorState={editorState}
        onChange={setEditorState}
        disabled={readOnly}
        className="beam-rich-text-controls"
      />,
    ],
    [editorState, readOnly],
  );

  const onEditorStateChange = (nextEditorState: EditorState) => {
    const raw = convertToRaw(nextEditorState.getCurrentContent());
    const markdown = nextEditorState.getCurrentContent().hasText()
      ? draftToMarkdown(raw).trim()
      : '';
    const text = raw.blocks.map((block) => block.text).join('\n');

    lastEmittedMarkdown.current = markdown;
    setEditorState(nextEditorState);
    onChange({text, markdown});
  };

  const isCheckin = variant === 'checkin';
  const defaultToolbar = {
    options: ['inline', 'list'],
    inline: {
      inDropdown: false,
      options: ['bold', 'italic', 'underline', 'strikethrough'],
      bold: {className: 'custom-toolbar-option'},
      italic: {className: 'custom-toolbar-option'},
      underline: {className: 'custom-toolbar-option'},
      strikethrough: {className: 'custom-toolbar-option'},
    },
    list: {
      inDropdown: false,
      options: ['unordered', 'ordered'],
      ordered: {className: 'custom-toolbar-option'},
      unordered: {className: 'custom-toolbar-option'},
    },
  };

  return (
    <Wrapper
      $error={error}
      $checkin={isCheckin}
      $readOnly={readOnly}
      $minHeight={minHeight}>
      <Editor
        ariaLabel={ariaLabel}
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        placeholder={placeholder}
        readOnly={readOnly}
        spellCheck
        toolbarHidden={readOnly}
        toolbar={isCheckin ? {options: []} : defaultToolbar}
        toolbarCustomButtons={isCheckin ? toolbarButtons : undefined}
        toolbarClassName={isCheckin ? 'beam-rich-text-toolbar' : undefined}
        toolbarStyle={
          isCheckin
            ? undefined
            : {
                border: 0,
                backgroundColor: 'white',
                borderRadius: '10px 10px 0 0',
              }
        }
        editorClassName="custom-editor-input"
        wrapperClassName="custom-editor-wrapper"
      />
      {helper && <Helper role="alert">{helper}</Helper>}
    </Wrapper>
  );
};
